import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowDownArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from 'src/components/PulseUI/Tooltip';
import { ClickAwayListener } from 'src/components/PulseUI/ClickAwayListener';
import type { SortDropdownOption, SortDropdownProps } from './types';

/**
 * The "by <sort>" dropdown for result lists, shared by Discovery and the Strategy
 * Center map results. It only renders grouped options and reports the pick; callers own
 * the sort state and build the groups (a metric group per header, or a single
 * header-less group).
 */
const SortDropdown = <T extends SortDropdownOption>({
  groups,
  selectedKey,
  onSelect,
}: SortDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const allOptions = useMemo(
    () => groups.flatMap((group) => group.options),
    [groups]
  );
  const selectedOption = allOptions.find(
    (option) => option.key === selectedKey
  );
  const hasGroupLabels = groups.some((group) => !!group.label);
  const triggerLabel = `by ${selectedOption?.label ?? allOptions[0]?.label ?? 'Sort by'}`;

  const handleClickAway = useCallback(() => setIsOpen(false), []);

  // Closing from the keyboard unmounts the focused option, so hand focus back to the
  // trigger. A click away doesn't, so it keeps focusing whatever was clicked.
  const closeAndFocusTrigger = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleSelect = (option: T) => {
    onSelect(option);
    closeAndFocusTrigger();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div
        className="relative"
        onKeyDown={(event) => {
          if (event.key === 'Escape') closeAndFocusTrigger();
        }}
        data-testid="sort-dropdown-container"
      >
        <button
          ref={triggerRef}
          type="button"
          className="flex items-center gap-2 h-10 cursor-pointer"
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Sort results"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="sort-options-list"
          data-testid="sort-dropdown-button"
        >
          <Tooltip
            content={triggerLabel}
            position="bottom"
            className="min-w-0 max-w-36"
          >
            <span
              data-testid="sort-dropdown-label"
              className="text-secondary text-sm font-normal font-['Inter'] leading-tight truncate block"
            >
              {triggerLabel}
            </span>
          </Tooltip>
          <div className="w-4 h-5 flex items-center justify-center">
            <Icon
              icon={faArrowDownArrowUp}
              className="text-secondary w-4 h-4 text-sm font-normal font-['Inter'] leading-tight"
              data-testid="sort-dropdown-icon"
            />
          </div>
        </button>

        {isOpen && (
          <div
            id="sort-options-list"
            role="listbox"
            aria-label="Sort options"
            className={twMerge(
              'absolute right-0 top-full z-50 w-auto max-h-60 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-lg',
              hasGroupLabels && 'min-w-56'
            )}
            data-testid="sort-dropdown-list"
          >
            {groups.map((group, index) => (
              <div
                key={group.id}
                role={group.label ? 'group' : undefined}
                aria-label={group.label ?? undefined}
                className={index === 0 ? '' : 'border-t border-gray-200'}
                data-testid={`sort-group-${group.id}`}
              >
                {group.label && (
                  <Tooltip content={group.label} position="bottom">
                    <div
                      className="px-4 pt-3 pb-1 text-xs font-semibold text-text-tertiary uppercase truncate max-w-64"
                      data-testid={`sort-group-header-${group.id}`}
                    >
                      {group.label}
                    </div>
                  </Tooltip>
                )}
                {group.options.map((option) => {
                  const isSelected = option.key === selectedOption?.key;
                  return (
                    <div
                      key={option.key}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      className={twMerge(
                        "px-4 py-2 text-secondary text-sm font-normal font-['Inter'] leading-tight whitespace-nowrap cursor-pointer",
                        group.label && 'px-6',
                        isSelected ? 'bg-sky-100' : 'hover:bg-gray-100'
                      )}
                      onClick={() => handleSelect(option)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelect(option);
                        }
                      }}
                      data-testid={`sort-option-${option.key}`}
                    >
                      {group.label
                        ? (option.optionLabel ?? option.label)
                        : option.label}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default SortDropdown;
